// src/contexts/LanguageContext.js
// Global language state. Wrap your app in <LanguageProvider> and use useLanguage() anywhere.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext(null);

export const SUPPORTED_LANGS = ['en', 'el', 'de'];

export function LanguageProvider({ children }) {
  // Default to whatever the user picked last time, or fall back to 'en'
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = localStorage.getItem('cosmoestates_lang');
    return SUPPORTED_LANGS.includes(saved) ? saved : 'en';
  });

  // Wrap setLang to guard against unsupported codes
  const setLang = useCallback((next) => {
    setLangState(SUPPORTED_LANGS.includes(next) ? next : 'en');
  }, []);

  // Persist the choice. Also self-heal localStorage if it holds a disabled language.
  useEffect(() => {
    localStorage.setItem('cosmoestates_lang', lang);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Cycle through supported languages (kept for callers that still use toggleLang)
  const toggleLang = useCallback(() => {
    setLangState((prev) => {
      const i = SUPPORTED_LANGS.indexOf(prev);
      const next = SUPPORTED_LANGS[(i + 1) % SUPPORTED_LANGS.length];
      return next;
    });
  }, []);

  // t('key.path') -> translated string. Falls back to English, then to the key itself.
  const t = useCallback(
    (key) => {
      const path = key.split('.');
      const lookup = (dict) => path.reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), dict);
      return lookup(translations[lang]) ?? lookup(translations.en) ?? key;
    },
    [lang]
  );

  const value = { lang, setLang, toggleLang, t, supportedLangs: SUPPORTED_LANGS };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used inside <LanguageProvider>');
  }
  return ctx;
}
